import {createAction} from '@reduxjs/toolkit'
import {
  CreateRsvpResponse,
  FormSubmission,
  Rsvp,
  RsvpStatus as RsvpStatusV2,
  UpdateRsvpResponse,
} from '@wix/ambassador-events-v2-rsvp/types'
import {RsvpStatus} from '@wix/events-types'
import {
  BI_ORIGINS,
  ChangeRsvpModalMode,
  MembersModal,
  getEventId,
  getEventTitle,
  isFull,
  isNoResponseEnabled,
  isRsvp,
  isRsvpStatusNo,
  isRsvpStatusWaitlist,
  isRsvpStatusYes,
  isTicketed,
} from '@wix/wix-events-commons-statics'
import {isEditor} from '../../../../commons/selectors/environment'
import {isDemoMode} from '../../../../commons/selectors/instance'
import {openDialogModal} from '../../../../commons/services/modal'
import {FormStep, RSVP_STEPS} from '../constants'
import {getCurrentMemberDetails, getCurrentMemberId} from '../selectors/current-member-details'
import {getEvent as getEventSelector, isDemoEvent} from '../selectors/event'
import {
  getMemberEventRsvpStatus,
  getMemberRsvpId,
  isMemberAbleToFinishRsvp,
  isMemberRsvpExists,
} from '../selectors/member-rsvp'
import {inRsvpForm} from '../selectors/navigation'
import {isRequestPending} from '../selectors/pending-requests'
import {hasAgreedWithPolicies, hasPolicies} from '../selectors/policies'
import {
  getCurrentStep,
  getFormSubmission,
  getRsvpDetails,
  isStepCompleted,
  isStepVisible,
  resolveGuestDetails,
  shouldNavigateToForm,
} from '../selectors/rsvp'
import {createAsyncAction} from '../services/redux-toolkit'
import {RsvpError} from '../types/errors'
import {RegFormData} from '../types/registration'
import {GetState, State, ThankYouMessageState} from '../types/state'
import {extractFormData} from '../utils/api-data-mapper'
import {getEvent, getMembers} from './event'
import {ensureLoginForMembersOnly, promptLogin} from './members'
import {navigateToChangeRsvp, navigateToForm, navigateToThankYouMessage, navigateToTicketsPicker} from './navigation'
import {registrationButtonClicked} from './registration'

export const getMemberRsvp = createAsyncAction<wix.events.rsvp.Rsvp | void, void>(
  'GET_MEMBER_RSVP',
  async (_params, {getState, dispatch, extra: {serverApi}}) => {
    const state = getState()

    const currentMemberId = getCurrentMemberId(state)
    const event = getEventSelector(state)

    if (currentMemberId && !isDemoEvent(state) && isRsvp(event)) {
      const response = await serverApi.getMemberRsvp(getEventId(event), currentMemberId)

      if (isMemberRsvpExists(getState()) && inRsvpForm(getState())) {
        dispatch(navigateToChangeRsvp())
      }

      return response
    }
  },
)

export const resetRsvpError = createAction<void>('RESET_RSVP_ERROR')

export const sendRsvpOld = createAsyncAction<wix.events.rsvp.CreateRsvpResponse, {eventId: string; guest: RegFormData}>(
  'SEND_RSVP_OLD',
  async ({eventId, guest}, {dispatch, getState, extra: {serverApi}}) => {
    const guestResponse = guest.response
    const currentMemberId = getCurrentMemberDetails(getState())?.id

    const response = await serverApi
      .createRsvp(eventId, extractFormData(guest) as unknown as RegFormData, guestResponse, currentMemberId)
      .catch((e: RsvpError) => {
        if (e.response?.data?.metadata?.member_already_registered) {
          dispatch(promptLogin())
        }

        throw e
      })

    if (response.rsvp?.id) {
      dispatch(navigateToThankYouMessage(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
    }

    return response
  },
  {
    condition: (_params, {getState}) => {
      const state = getState()

      return !isRequestPending(state, 'SEND_RSVP_OLD')
    },
  },
)

export const sendRsvp = createAsyncAction<CreateRsvpResponse, Rsvp>(
  'SEND_RSVP',
  async (rsvp, {dispatch, extra: {serverApi}}) => {
    const response = await serverApi.createRsvpV2(rsvp).catch(e => {
      if (e.response?.data?.details?.applicationError?.code === 'MEMBER_EMAIL_ALREADY_REGISTERED') {
        dispatch(promptLogin())
      }

      throw e
    })

    if (response.rsvp?.id) {
      dispatch(navigateToThankYouMessage(rsvp.status.toLowerCase() as ThankYouMessageState))
    }

    return response
  },
  {
    condition: (_params, {getState}) => {
      const state = getState()

      return !isRequestPending(state, 'SEND_RSVP')
    },
  },
)

export const updateRsvpOld = createAsyncAction<
  wix.events.rsvp.UpdateRsvpResponse,
  {eventId: string; guest: RegFormData}
>('UPDATE_RSVP_OLD', async ({eventId, guest}, {getState, dispatch, extra: {serverApi}}) => {
  const state = getState()

  const response = await serverApi.updateRsvp(
    eventId,
    extractFormData(guest) as unknown as RegFormData,
    guest.response,
    getMemberRsvpId(state),
  )

  if (response.rsvp?.id) {
    dispatch(navigateToThankYouMessage(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
  }

  return response
})

export const updateRsvp = createAsyncAction<UpdateRsvpResponse, Rsvp>(
  'UPDATE_RSVP',
  async (rsvp, {dispatch, extra: {serverApi}}) => {
    const response = await serverApi.updateRsvpV2(rsvp)

    if (response.rsvp?.id) {
      dispatch(navigateToThankYouMessage(rsvp.status.toLowerCase() as ThankYouMessageState))
    }

    return response
  },
)

export const updateRsvpStatus = createAsyncAction<void, {eventId: string; rsvpId: string; status: RsvpStatus}>(
  'UPDATE_RSVP_STATUS',
  async ({eventId, rsvpId, status}, {extra: {serverApi}}) => {
    return serverApi.updateRsvpStatus(eventId, rsvpId, status)
  },
)

export const deleteRsvp = createAsyncAction<void, {eventId: string; rsvpId: string}>(
  'DELETE_RSVP',
  async ({eventId, rsvpId}, {extra: {serverApi}}) => {
    return serverApi.deleteRsvp(eventId, rsvpId)
  },
)

export const updateRsvpOrNavigateToForm = (state: State, dispatch: Function, resolve: Function) => {
  const event = getEventSelector(state)
  const eventId = getEventId(event)
  const rsvpId = getMemberRsvpId(state)

  if (shouldNavigateToForm(state)) {
    dispatch(navigateToForm())
    return resolve(true)
  }

  dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.YES}))
}

export const changeMemberRsvp =
  (returnFocusElement: string) =>
  (dispatch: Function, getState: GetState): Promise<boolean> => {
    return new Promise(resolve => {
      const state = getState()
      const event = getEventSelector(state)
      const eventTitle = getEventTitle(event)
      const eventId = getEventId(event)
      const params = {event: eventTitle, eventId, origin: BI_ORIGINS.EVENT_DETAILS}

      if (isEditor(state)) {
        return dispatch(
          openDialogModal({
            type: MembersModal.NO_CANCEL_RSVP,
            params,
            onClose: () => resolve(false),
            returnFocusElement,
          }),
        )
      }

      const noEnabled = isNoResponseEnabled(event)
      const status = getMemberEventRsvpStatus(state)
      const mode = getChangeRsvpModalMode(state)

      return dispatch(
        openDialogModal({
          type: MembersModal.UPDATE_RSVP,
          params: {...params, mode},
          onClose: () => resolve(false),
          returnFocusElement,
          onConfirm: async () => {
            const rsvpId = getMemberRsvpId(state)

            if (!isMemberRsvpExists(state)) {
              dispatch(navigateToForm())

              return resolve(true)
            }

            if (noEnabled) {
              if (isRsvpStatusYes(status) || isRsvpStatusWaitlist(status)) {
                await dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.NO}))
              } else if (isRsvpStatusNo(getMemberEventRsvpStatus(state))) {
                await (isFull(event)
                  ? dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.WAITING}))
                  : updateRsvpOrNavigateToForm(state, dispatch, resolve))
              }
            } else {
              await dispatch(deleteRsvp({eventId, rsvpId}))
            }

            resolve(true)
            await dispatch(getEvent())
            dispatch(getMembers())
          },
        }),
      )
    })
  }

const getChangeRsvpModalMode = (state: State) => {
  const event = getEventSelector(state)
  const noEnabled = isNoResponseEnabled(event)
  const status = getMemberEventRsvpStatus(state)

  if (!isMemberRsvpExists(state)) {
    return ChangeRsvpModalMode.UPDATE_TO_YES
  }

  if (!noEnabled) {
    return ChangeRsvpModalMode.DELETE
  }

  if (isFull(event) && isRsvpStatusNo(status)) {
    return ChangeRsvpModalMode.WAITLIST
  }

  if (isRsvpStatusNo(status)) {
    return ChangeRsvpModalMode.UPDATE_TO_YES
  }

  return ChangeRsvpModalMode.UPDATE_TO_NO
}

export const handleRSVP = createAsyncAction<void>(
  'HANDLE_RSVP',
  async (_params, {getState, dispatch, extra: {flowAPI}}) => {
    const state = getState()
    const event = getEventSelector(state)

    dispatch(registrationButtonClicked())

    if (await dispatch(ensureLoginForMembersOnly())) {
      if (isMemberRsvpExists(getState())) {
        if (isMemberAbleToFinishRsvp(getState())) {
          await dispatch(navigateToForm())
        }

        return
      }
      if (flowAPI.environment.isMobile && isTicketed(event)) {
        await dispatch(navigateToTicketsPicker())
        return
      }

      dispatch(navigateToForm())
    }
  },
)

export const setRsvpDetailsOld = createAction<RegFormData>('SET_RSVP_DETAILS_OLD')

export const setFormSubmission = createAction<FormSubmission>('SET_FORM_SUBMISSION')

const rsvpOld = createAsyncAction<void, RegFormData | void>(
  'RSVP_OLD',
  async (data = undefined, {getState, dispatch}) => {
    const state = getState()
    const rsvpDetails = data || getRsvpDetails(state)
    const event = getEventSelector(state)
    const eventId = getEventId(event)

    if (isDemoEvent(state) || isDemoMode(state.instance)) {
      navigateToThankYouInDemoModeOld(dispatch, state, rsvpDetails.response)
      return
    }

    if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
      dispatch(updateRsvpOld({eventId, guest: rsvpDetails}))
    } else {
      dispatch(sendRsvpOld({eventId, guest: rsvpDetails}))
    }
  },
)

const rsvp = createAsyncAction<void, FormSubmission>('RSVP', async ({submissions, formId}, {dispatch, getState}) => {
  const state = getState()
  const event = getEventSelector(state)
  const eventId = getEventId(event)
  const memberId = getCurrentMemberId(state)
  const memberRsvpId = getMemberRsvpId(state)
  const rsvpDetails = resolveGuestDetails(event, submissions)

  if (isDemoEvent(state) || isDemoMode(state.instance)) {
    dispatch(navigateToThankYouInDemoMode(rsvpDetails.status))
    return
  }

  if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
    dispatch(updateRsvp({id: memberRsvpId, memberId, eventId, ...rsvpDetails, formSubmission: {formId, submissions}}))
  } else {
    dispatch(sendRsvp({memberId, eventId, ...rsvpDetails, formSubmission: {formId, submissions}}))
  }
})

const navigateToThankYouInDemoModeOld = (dispatch: Function, state: State, guestResponse: RsvpStatus) => {
  if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
    dispatch(navigateToThankYouMessage('yes'))
  } else {
    const thankYouMessageState: ThankYouMessageState =
      guestResponse === RsvpStatus.YES ? 'yes' : guestResponse === RsvpStatus.WAITING ? 'waiting' : 'no'

    dispatch(navigateToThankYouMessage(thankYouMessageState))
  }
}

const navigateToThankYouInDemoMode = createAsyncAction<void, RsvpStatusV2>(
  'NAVIGATE_TO_THANK_YOU_IN_DEMO_MODE',
  async (response, {dispatch, getState}) => {
    const state = getState()

    if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
      dispatch(navigateToThankYouMessage('yes'))
    } else {
      const thankYouMessageState: ThankYouMessageState =
        response === RsvpStatusV2.YES ? 'yes' : response === RsvpStatusV2.WAITLIST ? 'waitlist' : 'no'

      dispatch(navigateToThankYouMessage(thankYouMessageState))
    }
  },
)

export const submitRsvpStepOld = (data: any) => async (dispatch: Function, getState: GetState) => {
  const state = getState()
  const currentStep = getCurrentStep(state)

  switch (currentStep) {
    case FormStep.BuyerDetails: {
      if (hasPolicies(state)) {
        dispatch(setRsvpDetailsOld(data))
      } else {
        dispatch(rsvpOld(data))
      }
      break
    }
    case FormStep.Policies: {
      await dispatch(rsvpOld())
      break
    }
    default:
      break
  }

  dispatch(handleNextRsvpStep())
}

export const submitRsvpStep = createAsyncAction<void, FormSubmission>(
  'SUBMIT_RSVP_STEP',
  async ({submissions, formId}, {dispatch, getState}) => {
    const state = getState()
    const currentStep = getCurrentStep(state)

    switch (currentStep) {
      case FormStep.BuyerDetails: {
        if (hasPolicies(state)) {
          dispatch(setFormSubmission({submissions, formId}))
        } else {
          dispatch(rsvp({submissions, formId}))
        }
        break
      }
      case FormStep.Policies: {
        await dispatch(rsvp(getFormSubmission(state)))
        break
      }
      default:
        break
    }

    dispatch(handleNextRsvpStep())
  },
)

export const editRsvpStep = createAction<FormStep>('EDIT_RSVP_STEP')

export const handleNextRsvpStep = () => (dispatch: Function, getState: GetState) => {
  const state = getState()

  if (
    isStepCompleted({
      step: getCurrentStep(state),
      rsvpDetails: getRsvpDetails(state),
      formSubmission: getFormSubmission(state),
      agreedWithPolicies: hasAgreedWithPolicies(state),
    })
  ) {
    dispatch(nextStep())
  }
}

export const nextStep = createAsyncAction<{nextStep: FormStep}, void>('NEXT_RSVP_STEP', async (_params, {getState}) => {
  const state = getState()
  const {rsvpDetails, formSubmission} = state.rsvp

  const next = RSVP_STEPS.find(step => {
    const isVisible = isStepVisible({step, hasPolicies: hasPolicies(state)})
    const isCompleted = isStepCompleted({
      step,
      rsvpDetails,
      formSubmission,
      agreedWithPolicies: hasAgreedWithPolicies(state),
    })

    return isVisible && !isCompleted
  })

  const last = RSVP_STEPS[RSVP_STEPS.length - 1]

  return {
    nextStep: next ?? last,
  }
})

export const changeResponse = createAction<wix.events.rsvp.RsvpStatus>('CHANGE_RESPONSE')
